import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notifyError } from '../../../utils/notify';
import { triggerHaptic } from '../../../utils/telegram';
import {
  createFeedComment,
  createFeedReply,
  deleteFeedComment,
  getFeedComments,
  getFeedReplies,
  likeFeedComment,
  unlikeFeedComment,
} from '../../../store/slices/feed/asyncThunks';
import {
  selectFeedCommentsInfo,
  selectFeedPending,
  selectFeedRepliesByCommentId,
} from '../../../store/slices/feed/slice';
import { COMMENTS_LIMIT } from '../constants';
import { buildThreadedComments } from '../reelsPage.helpers';

const useReelComments = ({ reels, user }) => {
  const dispatch = useDispatch();
  const [activeCommentsReelId, setActiveCommentsReelId] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [replyTargetComment, setReplyTargetComment] = useState(null);
  const activeCommentsInfo = useSelector(state => selectFeedCommentsInfo(state, activeCommentsReelId));
  const repliesByCommentId = useSelector(selectFeedRepliesByCommentId);
  const feedPending = useSelector(selectFeedPending);

  const activeCommentsReel = useMemo(
    () => reels.find(reel => reel.id === activeCommentsReelId) || null,
    [activeCommentsReelId, reels],
  );

  const activeComments = useMemo(
    () => buildThreadedComments(activeCommentsInfo.data, repliesByCommentId, user),
    [activeCommentsInfo.data, repliesByCommentId, user],
  );

  useEffect(() => {
    if (!activeCommentsReelId || activeCommentsInfo.loaded || activeCommentsInfo.pending) {
      return;
    }

    dispatch(getFeedComments({ feedItemId: activeCommentsReelId, limit: COMMENTS_LIMIT }))
      .unwrap()
      .catch(error => {
        notifyError(error.message || 'Unable to load comments right now.');
      });
  }, [activeCommentsInfo.loaded, activeCommentsInfo.pending, activeCommentsReelId, dispatch]);

  const resetComposer = useCallback(() => {
    setCommentText('');
    setReplyTargetComment(null);
  }, []);

  const openComments = useCallback(
    reelId => {
      triggerHaptic();
      resetComposer();
      setActiveCommentsReelId(reelId);
    },
    [resetComposer],
  );

  const closeComments = useCallback(() => {
    resetComposer();
    setActiveCommentsReelId(null);
  }, [resetComposer]);

  const onCommentSubmit = useCallback(async () => {
    const trimmedValue = commentText.trim();

    if (!trimmedValue || !activeCommentsReel) {
      return;
    }

    triggerHaptic();

    try {
      if (replyTargetComment) {
        await dispatch(
          createFeedReply({
            feedCommentId: replyTargetComment.id,
            feedItemId: activeCommentsReel.id,
            text: trimmedValue,
          }),
        ).unwrap();
      } else {
        await dispatch(createFeedComment({ feedItemId: activeCommentsReel.id, text: trimmedValue })).unwrap();
      }

      resetComposer();
    } catch (error) {
      notifyError(error.message || 'Unable to post right now.');
    }
  }, [activeCommentsReel, commentText, dispatch, replyTargetComment, resetComposer]);

  const onCommentLikeToggle = useCallback(
    async comment => {
      if (!comment || comment.likePending) {
        return;
      }

      triggerHaptic();

      try {
        if (comment.liked) {
          await dispatch(unlikeFeedComment({ feedCommentId: comment.id })).unwrap();
        } else {
          await dispatch(likeFeedComment({ feedCommentId: comment.id })).unwrap();
        }
      } catch (error) {
        notifyError(error.message || 'Unable to update the comment like right now.');
      }
    },
    [dispatch],
  );

  const onReplyClick = useCallback(comment => {
    triggerHaptic();
    setReplyTargetComment(comment);
  }, []);

  const onCancelReply = useCallback(() => {
    setReplyTargetComment(null);
  }, []);

  const onDeleteComment = useCallback(
    async comment => {
      if (!activeCommentsReel || !window.confirm('Delete this comment?')) {
        return;
      }

      triggerHaptic();

      try {
        await dispatch(
          deleteFeedComment({
            feedCommentId: comment.id,
            feedItemId: activeCommentsReel.id,
            parentCommentId: comment.parentCommentId,
          }),
        ).unwrap();

        if (replyTargetComment?.id === comment.id || replyTargetComment?.parentCommentId === comment.id) {
          setReplyTargetComment(null);
        }
      } catch (error) {
        notifyError(error.message || 'Unable to delete this comment right now.');
      }
    },
    [activeCommentsReel, dispatch, replyTargetComment],
  );

  const onLoadMoreComments = useCallback(async () => {
    if (!activeCommentsReelId || !activeCommentsInfo.hasMore || !activeCommentsInfo.nextCursor) {
      return;
    }

    try {
      await dispatch(
        getFeedComments({
          feedItemId: activeCommentsReelId,
          limit: COMMENTS_LIMIT,
          cursor: activeCommentsInfo.nextCursor,
        }),
      ).unwrap();
    } catch (error) {
      notifyError(error.message || 'Unable to load more comments right now.');
    }
  }, [activeCommentsInfo.hasMore, activeCommentsInfo.nextCursor, activeCommentsReelId, dispatch]);

  const onLoadReplies = useCallback(
    async commentId => {
      try {
        await dispatch(getFeedReplies({ feedCommentId: commentId, limit: COMMENTS_LIMIT })).unwrap();
      } catch (error) {
        notifyError(error.message || 'Unable to load replies right now.');
      }
    },
    [dispatch],
  );

  const onLoadMoreReplies = useCallback(
    async commentId => {
      const repliesInfo = repliesByCommentId[commentId];

      if (!repliesInfo?.hasMore || !repliesInfo?.nextCursor) {
        return;
      }

      try {
        await dispatch(
          getFeedReplies({
            feedCommentId: commentId,
            limit: COMMENTS_LIMIT,
            cursor: repliesInfo.nextCursor,
          }),
        ).unwrap();
      } catch (error) {
        notifyError(error.message || 'Unable to load more replies right now.');
      }
    },
    [dispatch, repliesByCommentId],
  );

  return {
    activeCommentsReel,
    commentsCount: activeCommentsReel?.stats.commentsCount || 0,
    comments: activeComments,
    isCommentsOpen: Boolean(activeCommentsReel),
    isLoading: !activeCommentsInfo.loaded && !activeComments.length,
    isSubmitting: activeCommentsInfo.createPending || feedPending.createFeedReply || feedPending.deleteFeedComment,
    composerPlaceholder: replyTargetComment
      ? `Reply to ${replyTargetComment.user?.name || 'User'}...`
      : 'Add comment...',
    replyTargetName: replyTargetComment?.user?.name || '',
    commentText,
    setCommentText,
    openComments,
    closeComments,
    onCommentSubmit,
    onCommentLikeToggle,
    onReplyClick,
    onCancelReply,
    onDeleteComment,
    onLoadMoreComments,
    commentsHasMore: Boolean(activeCommentsInfo.hasMore && activeCommentsInfo.nextCursor),
    commentsFetchMorePending: activeCommentsInfo.fetchMorePending,
    onLoadReplies,
    onLoadMoreReplies,
  };
};

export default useReelComments;
